/**
 * Compare two people (screenshots/46, brief §2.9; feature `compare`, everyone). /dashboard/people/compare?a=&b=
 * is shareable. Two pickers + swap; "On the record this term" rows; three equal cards: political leaning (both on
 * one meter, AI estimate), how often they vote together (where they split most), roles on the Council.
 * Paints from snapshot:compare; picking someone refetches and updates the URL. Agreement, where they split
 * and the range-overlap rule come from compareLogic.ts (tests/screens.test.ts).
 */
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowLeftRight, ArrowRight, ChevronsUpDown, Search, Share } from "lucide-react";
import { Face } from "../components/shared/Face";
import { toastStore } from "../stores/toastStore";
import { AiEstimateChip } from "./LeaningMeter";
import { PersonImg, profileHref } from "./atoms";
import { leaningLabel, leaningToPct } from "./types";
import { peopleApi } from "./peopleStore";
import type { CompareData, CompareSide } from "./model";
import { agreement, compareHref, rangesOverlap, splitsByTopic } from "./compareLogic";
import { navigate } from "../app/router";
import { useDismiss } from "../hooks/useDismiss";
import { Crumbs, sectionCrumb } from "../app/crumbs";

const initials = (s: CompareSide) => `${s.name[0]}${s.short[0]}`.toUpperCase();
const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export function Compare({ initial }: { initial: CompareData | null }) {
  const [data, setData] = useState<CompareData | null>(initial);
  const load = async (a: string | null, b: string | null) => {
    const q = new URLSearchParams();
    if (a) q.set("a", a);
    if (b) q.set("b", b);
    history.replaceState(null, "", a && b ? compareHref(a, b) : `/dashboard/people/compare?${q}`);
    setData(await peopleApi(`compare?${q}`).catch(() => data));
  };
  useEffect(() => { if (!initial) { const p = new URLSearchParams(location.search); load(p.get("a"), p.get("b")); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const { a, b } = data;
  const ag = a?.votes && b?.votes ? agreement(a.votes, b.votes) : null;
  const splits = a?.votes && b?.votes ? splitsByTopic(a.votes, b.votes, data.categories) : [];
  return (
    <div className="profile-page p-comparepage">
      <Crumbs trail={[sectionCrumb("people"), { label: "Compare" }]} />
      <header className="page-header">
        <div className="page-header__title">
          <h1 className="page-title">Compare</h1>
        </div>
        <button className="btn" onClick={() => { navigator.clipboard?.writeText(location.href); toastStore.getState().show({ message: "Link copied" }); }}><Share size={14} aria-hidden />Share comparison</button>
      </header>
      <div className="p-pickers">
        <Picker side={a} options={data.options} onPick={(id) => load(id, b?.id ?? null)} />
        <button className="p-swap" aria-label="Swap" onClick={() => load(b?.id ?? null, a?.id ?? null)}><ArrowLeftRight size={18} /></button>
        <Picker side={b} options={data.options} onPick={(id) => load(a?.id ?? null, id)} />
      </div>
      {a && b && <>
        <section className="p-record">
          <span className="p-eyebrow">ON THE RECORD THIS TERM</span>
          {a.record || b.record ? <>
            <Row label="Attendance" a={a.record && `${fmt(a.record.attendancePct)}%`} b={b.record && `${fmt(b.record.attendancePct)}%`} note={`of ${(a.record ?? b.record)!.rollCalls.toLocaleString("en-US")} key roll calls`} />
            <Row label="Nay votes" a={a.record?.nays} b={b.record?.nays} note="this term" />
            <Row label="With the majority" a={a.record && `${fmt(a.record.withMajorityPct)}%`} b={b.record && `${fmt(b.record.withMajorityPct)}%`} note="on divided votes" />
            <Row label="Filed as lead sponsor" a={a.record?.filed} b={b.record?.filed}
              note={[a.record && `${a.short} ${a.record.passed} passed`, b.record && `${b.short} ${b.record.passed} passed`].filter(Boolean).join(" · ")} />
          </> : <>
            <Row label="Meetings spoken at" a={a.stats.meetings} b={b.stats.meetings} note="" />
            <Row label="Moments on record" a={a.stats.moments} b={b.stats.moments} note="" />
            <Row label="Positions taken" a={a.stats.positions} b={b.stats.positions} note="" />
          </>}
        </section>
        <div className="p-cmp-cards">
          <LeaningCompare a={a} b={b} />
          <section className="p-card p-cmp-card">
            <h3>How often they vote together</h3>
            {ag ? <>
              <div className="p-together"><b>{ag.pct ?? "—"}{ag.pct !== null && "%"}</b><span>same vote on {ag.same} of {ag.both} divided roll calls</span></div>
              {splits.length > 0 && <>
                <span className="p-eyebrow">WHERE THEY SPLIT MOST</span>
                {splits.map((s) => <div key={s.topic} className="p-split"><span>{s.topic}</span><b>{s.split} of {s.of} split</b></div>)}
              </>}
            </> : <SharedPositions a={a} b={b} />}
          </section>
          <section className="p-card p-cmp-card">
            <h3>Roles on the Council</h3>
            {[a, b].map((s) => (
              <div key={s.id} className="p-role">
                <PersonImg image={s.image} name={s.name} size={32} w={40} h={28} />
                <span>
                  <b>{s.roles.chairs.length ? `Chair · ${s.roles.chairs[0]}` : s.sub}</b>
                  <small>{s.roles.chairs.length ? `Member of ${s.roles.member + s.roles.chairs.length - 1} more committees` : s.roles.member ? `Member of ${s.roles.member} committees` : ""}</small>
                </span>
              </div>
            ))}
            <button className="link-arrow" onClick={() => { window.open(profileHref(b.id), "_blank"); navigate(profileHref(a.id)); }}>Open both profiles<ArrowRight size={14} aria-hidden /></button>
          </section>
        </div>
      </>}
    </div>
  );
}

function Row({ label, a, b, note }: { label: string; a: React.ReactNode; b: React.ReactNode; note: string }) {
  return <div className="p-record__row"><span>{label}</span><b>{a ?? "—"}</b><b>{b ?? "—"}</b><small>{note}</small></div>;
}

function Picker({ side, options, onPick }: { side: CompareSide | null; options: CompareData["options"]; onPick: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, open, () => setOpen(false));   // outside press or Esc
  const list = options.filter((o) => !q || `${o.name} ${o.sub}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div ref={ref} className="p-picker">
      <button className="p-picker__btn" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}>
        {side ? <PersonImg image={side.image} name={side.name} size={44} w={56} h={40} /> : <span className="p-picker__empty" />}
        <span className="p-picker__who"><b>{side?.name ?? "Choose a person"}</b><small>{side?.sub}</small></span>
        <ChevronsUpDown size={16} aria-hidden />
      </button>
      {open && (
        <div className="p-pop p-picker__menu">
          <label className="p-search"><Search size={14} aria-hidden /><input autoFocus aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} /></label>
          <ul role="listbox">
            {list.map((o) => (
              <li key={o.id} role="option" aria-selected={o.id === side?.id}>
                <button onClick={() => { setOpen(false); onPick(o.id); }}><Face name={o.name} photoUrl={o.photoUrl} size={24} /><span><b>{o.name}</b><small>{o.sub}</small></span></button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function LeaningCompare({ a, b }: { a: CompareSide; b: CompareSide }) {
  const la = a.leaning.status === "estimated" ? a.leaning : null, lb = b.leaning.status === "estimated" ? b.leaning : null;
  let text = "";
  if (la && lb) {
    const x = leaningLabel(la.score).toLowerCase(), y = leaningLabel(lb.score).toLowerCase();
    const overlap = rangesOverlap(la, lb);
    text = `${x === y ? `Both estimated ${x}.` : `${a.short} estimated ${x}, ${b.short} ${y}.`} ${overlap ? "Ranges overlap, so the difference isn't meaningful." : "Ranges don't overlap."}`;
  }
  return (
    <section className="p-card p-cmp-card">
      <div className="row-gap"><h3>Political leaning</h3><AiEstimateChip /></div>
      <div className="leaning leaning--full p-dual" role="img" aria-label={`AI estimates: ${a.name} and ${b.name}`}>
        <div className="leaning__track">
          {la && <span className="leaning__band" style={{ left: `${leaningToPct(la.low)}%`, width: `${leaningToPct(la.high) - leaningToPct(la.low)}%` }} />}
          {lb && <span className="leaning__band" style={{ left: `${leaningToPct(lb.low)}%`, width: `${leaningToPct(lb.high) - leaningToPct(lb.low)}%` }} />}
          {la && <span className="p-dual__mark p-dual__mark--a" style={{ left: `${leaningToPct(la.score)}%` }}>{initials(a)}</span>}
          {lb && <span className="p-dual__mark p-dual__mark--b" style={{ left: `${leaningToPct(lb.score)}%` }}>{initials(b)}</span>}
        </div>
        <div className="leaning__scale"><span>Progressive</span><span>Moderate</span><span>Conservative</span></div>
      </div>
      {text && <p className="p-cmp-text">{text}</p>}
    </section>
  );
}

function SharedPositions({ a, b }: { a: CompareSide; b: CompareSide }) {
  const shared = a.positions.filter((p) => b.positions.some((q) => q.id === p.id));
  const agree = shared.filter((p) => b.positions.find((q) => q.id === p.id)!.stance === p.stance).length;
  return <div className="p-together"><b>{shared.length ? Math.round((agree / shared.length) * 100) : 0}%</b><span>Agree on {agree} of {shared.length} shared items</span></div>;
}
